import { useEffect, useRef, useState } from "react"
import { ExcelClient } from "@/domain/excel/client/ExcelClient"
import {
  XLSX_MIME_TYPE,
  downloadBytes,
} from "@/domain/common/utils/FileUtils"
import type { ExcelKindPayload } from "@bracket/idl/main/excel/payload/ExcelPayloadPt"

/**
 * 엑셀 출력 훅 (신청 선수 · 대진표 공용).
 *
 * 서버가 요청 한 번에 파일을 만들어 내려주므로 화면은 **부르고 저장한다**.
 * 버튼은 `isExporting` 동안 잠근다 — 진행률을 보여줄 단계가 없다.
 */
export function useExcelExport() {
  const [isExporting, setIsExporting] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  /** 화면을 떠난 뒤에 상태를 갱신하지 않도록 표시해 둔다 */
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
    }
  }, [])

  /**
   * 엑셀을 만들어 내려받는다
   *
   * 실패하면 서버가 준 문구를 그대로 화면에 남긴다 — 화면이 이유를 만들어내지 않는다.
   */
  async function exportExcel(kind: ExcelKindPayload, tournamentId: string) {
    setIsExporting(true)
    setErrorMessage("")

    try {
      const exported = await ExcelClient.export(kind, tournamentId)
      downloadBytes(exported.fileName, exported.content, XLSX_MIME_TYPE)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "엑셀 출력에 실패했습니다.",
      )
    } finally {
      if (isMountedRef.current) setIsExporting(false)
    }
  }

  return {
    isExporting,
    errorMessage,
    exportExcel,
  }
}
